import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import DataTable, { TableColumn } from 'react-data-table-component';
import {
    Typography,
    Paper,
    Card,
    CardContent,
    Button,
    Chip,
    Box,
    TextField,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Snackbar,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { Mail, Send, Clock, Users, History, Plus, X, AlertTriangle, CheckCircle, XCircle, Lock } from 'lucide-react';
import { getApiBaseUrl } from '../utils/baseUrl';
import { useLoading, acquireTokenSafe } from '../App';

interface ScheduledEmailConfig {
    Id: number;
    EmailType: string;
    Subject: string;
    Recipients: string;
    ScheduleDay: number;
    ScheduleHour: number;
    Enabled: boolean;
    CreatedAt: string;
    UpdatedAt: string;
    LastSentAt: string | null;
    LastStatus: string | null;
    LastError: string | null;
}

interface EmailHistory {
    Id: number;
    SentAt: string;
    Status: string;
    ErrorMessage: string | null;
    RecipientsCount: number;
    IsTestEmail: boolean;
}

const DAYS_OF_WEEK = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i.toString().padStart(2, '0')}:00 UTC`,
}));

const EMAIL_TYPE_LABELS: Record<string, string> = {
    'ClientHealthWeekly': 'Weekly Client Health Report',
};

const ScheduledEmails: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [configs, setConfigs] = useState<ScheduledEmailConfig[]>([]);
    const [selectedConfig, setSelectedConfig] = useState<ScheduledEmailConfig | null>(null);
    const [history, setHistory] = useState<EmailHistory[]>([]);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [testRecipient, setTestRecipient] = useState('');
    const [newRecipient, setNewRecipient] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [hasAccess, setHasAccess] = useState<boolean | null>(null);
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [sendingTest, setSendingTest] = useState(false);

    const getAuthHeader = async () => {
        const token = await acquireTokenSafe(instance, accounts);
        if (!token) return null;
        return `Bearer ${token}`;
    };

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const authHeader = await getAuthHeader();
            if (!authHeader) {
                setHasAccess(false);
                return;
            }

            const response = await fetch(`${getApiBaseUrl()}/api/scheduled-emails`, {
                headers: { Authorization: authHeader },
            });

            if (response.status === 403) {
                setHasAccess(false);
                return;
            }

            if (!response.ok) throw new Error('Failed to fetch email configurations');

            const data = await response.json();
            setConfigs(data);
            setHasAccess(true);
        } catch (err: any) {
            console.error('Error fetching configs:', err);
            showSnackbar(err.message || 'Failed to fetch email configurations', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async (configId: number) => {
        try {
            const authHeader = await getAuthHeader();
            if (!authHeader) return;

            const response = await fetch(`${getApiBaseUrl()}/api/scheduled-emails/${configId}/history`, {
                headers: { Authorization: authHeader },
            });

            if (!response.ok) throw new Error('Failed to fetch email history');

            const data = await response.json();
            setHistory(data);
        } catch (err: any) {
            console.error('Error fetching history:', err);
            showSnackbar(err.message || 'Failed to fetch email history', 'error');
        }
    };

    const updateConfig = async (config: ScheduledEmailConfig) => {
        try {
            const authHeader = await getAuthHeader();
            if (!authHeader) {
                showSnackbar('Authentication required. Please refresh the page.', 'error');
                return;
            }

            let recipients: string[];
            try {
                recipients = JSON.parse(config.Recipients || '[]');
            } catch (parseError) {
                console.error('Error parsing recipients:', parseError);
                recipients = [];
            }

            const response = await fetch(`${getApiBaseUrl()}/api/scheduled-emails/${config.Id}`, {
                method: 'PUT',
                headers: {
                    Authorization: authHeader,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    subject: config.Subject,
                    recipients: recipients,
                    scheduleDay: config.ScheduleDay,
                    scheduleHour: config.ScheduleHour,
                    enabled: config.Enabled,
                }),
            });

            if (!response.ok) {
                const errorData = await response.text();
                console.error('Server error:', response.status, errorData);
                throw new Error(`Failed to update configuration: ${response.status}`);
            }

            showSnackbar('Configuration updated successfully', 'success');
            fetchConfigs();
        } catch (err: any) {
            console.error('Error updating config:', err);
            showSnackbar(err.message || 'Failed to update configuration', 'error');
        }
    };

    const sendTestEmail = async () => {
        if (!selectedConfig || !testRecipient) return;

        setSendingTest(true);
        try {
            const authHeader = await getAuthHeader();
            if (!authHeader) return;

            const response = await fetch(`${getApiBaseUrl()}/api/scheduled-emails/${selectedConfig.Id}/test`, {
                method: 'POST',
                headers: {
                    Authorization: authHeader,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ testRecipient }),
            });

            const data = await response.json();

            if (!response.ok) throw new Error(data.error || 'Failed to send test email');

            showSnackbar(`Test email sent to ${testRecipient}`, 'success');
            setTestDialogOpen(false);
            setTestRecipient('');
        } catch (err: any) {
            console.error('Error sending test email:', err);
            showSnackbar(err.message || 'Failed to send test email', 'error');
        } finally {
            setSendingTest(false);
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const addRecipient = (config: ScheduledEmailConfig) => {
        if (!newRecipient || !newRecipient.includes('@')) {
            showSnackbar('Please enter a valid email address', 'error');
            return;
        }

        const recipients = JSON.parse(config.Recipients || '[]');
        if (recipients.includes(newRecipient)) {
            showSnackbar('Email already in recipients list', 'error');
            return;
        }

        recipients.push(newRecipient);
        const updatedConfig = { ...config, Recipients: JSON.stringify(recipients) };
        updateConfig(updatedConfig);
        setNewRecipient('');
    };

    const removeRecipient = (config: ScheduledEmailConfig, email: string) => {
        const recipients = JSON.parse(config.Recipients || '[]');
        const filtered = recipients.filter((r: string) => r !== email);
        const updatedConfig = { ...config, Recipients: JSON.stringify(filtered) };
        updateConfig(updatedConfig);
    };

    const handleConfigChange = (configId: number, field: keyof ScheduledEmailConfig, value: any) => {
        setConfigs(prev =>
            prev.map(c => (c.Id === configId ? { ...c, [field]: value } : c))
        );
    };

    const openHistoryDialog = (config: ScheduledEmailConfig) => {
        setSelectedConfig(config);
        fetchHistory(config.Id);
        setHistoryDialogOpen(true);
    };

    const openTestDialog = (config: ScheduledEmailConfig) => {
        setSelectedConfig(config);
        setTestDialogOpen(true);
    };

    useEffect(() => {
        if (accounts.length > 0) {
            fetchConfigs();
        }
    }, [accounts]);

    const formatDateTime = (dateStr: string | null) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString('en-IE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const historyColumns: TableColumn<EmailHistory>[] = [
        {
            name: 'Sent At',
            selector: row => row.SentAt,
            sortable: true,
            cell: row => formatDateTime(row.SentAt),
        },
        {
            name: 'Status',
            selector: row => row.Status,
            cell: row => (
                <Chip
                    size="small"
                    label={row.Status}
                    icon={row.Status === 'Success' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    sx={{
                        backgroundColor: row.Status === 'Success' ? 'rgba(63, 185, 80, 0.2)' : 'rgba(248, 81, 73, 0.2)',
                        color: row.Status === 'Success' ? '#3fb950' : '#f85149',
                        '& .MuiChip-icon': {
                            color: 'inherit',
                        },
                    }}
                />
            ),
        },
        {
            name: 'Recipients',
            selector: row => row.RecipientsCount,
            center: true,
        },
        {
            name: 'Type',
            cell: row => (
                row.IsTestEmail ? (
                    <Chip size="small" label="Test" sx={{ backgroundColor: 'rgba(88, 166, 255, 0.2)', color: '#58a6ff' }} />
                ) : (
                    <Chip size="small" label="Scheduled" sx={{ backgroundColor: 'rgba(139, 148, 158, 0.2)', color: '#8b949e' }} />
                )
            ),
            center: true,
        },
        {
            name: 'Error',
            selector: row => row.ErrorMessage || '',
            cell: row => row.ErrorMessage ? (
                <span style={{ color: '#f85149', fontSize: '12px' }}>{row.ErrorMessage}</span>
            ) : '—',
            grow: 2,
        },
    ];

    if (hasAccess === false) {
        return (
            <div className="page-container">
                <div className="access-denied">
                    <Lock size={48} />
                    <h2>Access Restricted</h2>
                    <p>You don't have permission to view Scheduled Emails.</p>
                    <p>Please contact your administrator if you believe this is an error.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <Mail size={20} />
                    <h1>Scheduled Emails</h1>
                    <span className="restricted-badge">
                        <Lock size={12} />
                        Restricted
                    </span>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {configs.map(config => {
                    const recipients = JSON.parse(config.Recipients || '[]');
                    
                    return (
                        <Card
                            key={config.Id}
                            sx={{
                                backgroundColor: 'var(--bg-secondary)',
                                border: '1px solid var(--border-primary)',
                                borderRadius: '8px',
                            }}
                        >
                            <CardContent sx={{ padding: '24px' }}>
                                {/* Header Row */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600, mb: 0.5 }}>
                                            {EMAIL_TYPE_LABELS[config.EmailType] || config.EmailType}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                                            {config.EmailType}
                                        </Typography>
                                    </Box>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={config.Enabled}
                                                onChange={(e) => {
                                                    handleConfigChange(config.Id, 'Enabled', e.target.checked);
                                                    updateConfig({ ...config, Enabled: e.target.checked });
                                                }}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: '#3fb950',
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: '#3fb950',
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ color: config.Enabled ? '#3fb950' : 'var(--text-muted)' }}>
                                                {config.Enabled ? 'Enabled' : 'Disabled'}
                                            </Typography>
                                        }
                                    />
                                </Box>

                                {/* Subject */}
                                <Box sx={{ mb: 3 }}>
                                    <TextField
                                        fullWidth
                                        label="Subject"
                                        value={config.Subject}
                                        onChange={(e) => handleConfigChange(config.Id, 'Subject', e.target.value)}
                                        onBlur={() => updateConfig(config)}
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'var(--bg-tertiary)',
                                                color: 'var(--text-primary)',
                                                '& fieldset': { borderColor: 'var(--border-primary)' },
                                                '&:hover fieldset': { borderColor: 'var(--border-focus)' },
                                            },
                                            '& .MuiInputLabel-root': { color: 'var(--text-secondary)' },
                                        }}
                                    />
                                </Box>

                                {/* Schedule Row */}
                                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel sx={{ color: 'var(--text-secondary)' }}>Day</InputLabel>
                                        <Select
                                            value={config.ScheduleDay}
                                            label="Day"
                                            onChange={(e) => {
                                                handleConfigChange(config.Id, 'ScheduleDay', e.target.value);
                                                updateConfig({ ...config, ScheduleDay: e.target.value as number });
                                            }}
                                            sx={{
                                                backgroundColor: 'var(--bg-tertiary)',
                                                color: 'var(--text-primary)',
                                                '& fieldset': { borderColor: 'var(--border-primary)' },
                                            }}
                                        >
                                            {DAYS_OF_WEEK.map(day => (
                                                <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel sx={{ color: 'var(--text-secondary)' }}>Time (UTC)</InputLabel>
                                        <Select
                                            value={config.ScheduleHour}
                                            label="Time (UTC)"
                                            onChange={(e) => {
                                                handleConfigChange(config.Id, 'ScheduleHour', e.target.value);
                                                updateConfig({ ...config, ScheduleHour: e.target.value as number });
                                            }}
                                            sx={{
                                                backgroundColor: 'var(--bg-tertiary)',
                                                color: 'var(--text-primary)',
                                                '& fieldset': { borderColor: 'var(--border-primary)' },
                                            }}
                                        >
                                            {HOURS.map(hour => (
                                                <MenuItem key={hour.value} value={hour.value}>{hour.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'var(--text-secondary)' }}>
                                        <Clock size={16} />
                                        <Typography variant="body2">
                                            Runs every {DAYS_OF_WEEK.find(d => d.value === config.ScheduleDay)?.label} at {HOURS.find(h => h.value === config.ScheduleHour)?.label}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Recipients Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Users size={16} />
                                        Recipients ({recipients.length})
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {recipients.length === 0 ? (
                                            <Typography variant="body2" sx={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                                No recipients configured. Add recipients to enable sending.
                                            </Typography>
                                        ) : (
                                            recipients.map((email: string) => (
                                                <Chip
                                                    key={email}
                                                    label={email}
                                                    onDelete={() => removeRecipient(config, email)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'var(--bg-tertiary)',
                                                        color: 'var(--text-primary)',
                                                        '& .MuiChip-deleteIcon': {
                                                            color: 'var(--text-muted)',
                                                            '&:hover': { color: '#f85149' },
                                                        },
                                                    }}
                                                />
                                            ))
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            placeholder="Add recipient email"
                                            value={newRecipient}
                                            onChange={(e) => setNewRecipient(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addRecipient(config);
                                                }
                                            }}
                                            sx={{
                                                flex: 1,
                                                maxWidth: 300,
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'var(--bg-tertiary)',
                                                    color: 'var(--text-primary)',
                                                    '& fieldset': { borderColor: 'var(--border-primary)' },
                                                },
                                            }}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<Plus size={16} />}
                                            onClick={() => addRecipient(config)}
                                            sx={{
                                                borderColor: 'var(--border-primary)',
                                                color: 'var(--text-primary)',
                                                '&:hover': {
                                                    borderColor: 'var(--accent-blue)',
                                                    backgroundColor: 'rgba(88, 166, 255, 0.1)',
                                                },
                                            }}
                                        >
                                            Add
                                        </Button>
                                    </Box>
                                </Box>

                                {/* Last Send Status */}
                                {config.LastSentAt && (
                                    <Box sx={{ mb: 3, p: 2, backgroundColor: 'var(--bg-tertiary)', borderRadius: '6px' }}>
                                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
                                            Last sent: {formatDateTime(config.LastSentAt)}
                                        </Typography>
                                        {config.LastStatus && (
                                            <Chip
                                                size="small"
                                                label={config.LastStatus}
                                                icon={config.LastStatus === 'Success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                                                sx={{
                                                    backgroundColor: config.LastStatus === 'Success' ? 'rgba(63, 185, 80, 0.2)' : 'rgba(248, 81, 73, 0.2)',
                                                    color: config.LastStatus === 'Success' ? '#3fb950' : '#f85149',
                                                    '& .MuiChip-icon': { color: 'inherit' },
                                                }}
                                            />
                                        )}
                                        {config.LastError && (
                                            <Typography variant="body2" sx={{ color: '#f85149', mt: 1 }}>
                                                {config.LastError}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {/* Action Buttons */}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<Send size={16} />}
                                        onClick={() => openTestDialog(config)}
                                        sx={{
                                            backgroundColor: 'var(--accent-blue)',
                                            '&:hover': { backgroundColor: '#4593e6' },
                                        }}
                                    >
                                        Send Test Email
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<History size={16} />}
                                        onClick={() => openHistoryDialog(config)}
                                        sx={{
                                            borderColor: 'var(--border-primary)',
                                            color: 'var(--text-primary)',
                                            '&:hover': {
                                                borderColor: 'var(--accent-blue)',
                                                backgroundColor: 'rgba(88, 166, 255, 0.1)',
                                            },
                                        }}
                                    >
                                        View History
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Test Email Dialog */}
            <Dialog
                open={testDialogOpen}
                onClose={() => setTestDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-primary)',
                        minWidth: 400,
                    },
                }}
            >
                <DialogTitle sx={{ color: 'var(--text-primary)' }}>
                    Send Test Email
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 2 }}>
                        Enter your email address to receive a test version of this email.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Test Recipient Email"
                        type="email"
                        value={testRecipient}
                        onChange={(e) => setTestRecipient(e.target.value)}
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'var(--bg-tertiary)',
                                color: 'var(--text-primary)',
                                '& fieldset': { borderColor: 'var(--border-primary)' },
                            },
                            '& .MuiInputLabel-root': { color: 'var(--text-secondary)' },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setTestDialogOpen(false)}
                        sx={{ color: 'var(--text-secondary)' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={sendTestEmail}
                        disabled={!testRecipient || sendingTest}
                        startIcon={<Send size={16} />}
                        sx={{
                            backgroundColor: 'var(--accent-blue)',
                            '&:hover': { backgroundColor: '#4593e6' },
                        }}
                    >
                        {sendingTest ? 'Sending...' : 'Send Test'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History Dialog */}
            <Dialog
                open={historyDialogOpen}
                onClose={() => setHistoryDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-primary)',
                    },
                }}
            >
                <DialogTitle sx={{ color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Email Send History
                    <IconButton onClick={() => setHistoryDialogOpen(false)} sx={{ color: 'var(--text-muted)' }}>
                        <X size={20} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DataTable
                        columns={historyColumns}
                        data={history}
                        pagination
                        paginationPerPage={10}
                        noDataComponent={
                            <Box sx={{ p: 4, textAlign: 'center', color: 'var(--text-muted)' }}>
                                No email history found
                            </Box>
                        }
                        customStyles={{
                            table: {
                                style: {
                                    backgroundColor: 'var(--bg-secondary)',
                                },
                            },
                            headRow: {
                                style: {
                                    backgroundColor: 'var(--bg-tertiary)',
                                    borderBottomColor: 'var(--border-primary)',
                                },
                            },
                            headCells: {
                                style: {
                                    color: 'var(--text-secondary)',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                },
                            },
                            rows: {
                                style: {
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderBottomColor: 'var(--border-primary)',
                                    '&:hover': {
                                        backgroundColor: 'var(--bg-hover)',
                                    },
                                },
                            },
                            cells: {
                                style: {
                                    color: 'var(--text-primary)',
                                },
                            },
                            pagination: {
                                style: {
                                    backgroundColor: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    borderTopColor: 'var(--border-primary)',
                                },
                            },
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ScheduledEmails;
